import { useMemo, useState } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CategoryRule, ProposedChange } from "./api"
import { Pattern, PatternSegment, is_valid_literal, pattern_matches, pattern_to_english, pattern_to_regex, regex_to_pattern } from "./pattern"

interface RuleDialogProps {
  open: boolean
  /** Rule being edited, or null to create a new rule. */
  rule: CategoryRule | null
  /** Source for new rules (from the page's source filter). */
  default_source: string
  sources: string[]
  /** Existing category names, offered as suggestions for the name field. */
  category_names: string[]
  /** Recent invoice numbers to try patterns against, keyed by source. */
  example_invoice_numbers: string[]
  onClose: () => void
  /** Hands the finished change off to the preview-and-confirm dialog. */
  onPreview: (change: ProposedChange, title: string) => void
}

/** Create or edit a rule: category name/description plus the matching patterns. */
export function RuleDialog({ open, rule, default_source, sources, category_names, example_invoice_numbers, onClose, onPreview }: RuleDialogProps) {
  const [name, setName] = useState(rule?.name ?? "")
  const [description, setDescription] = useState(rule?.description ?? "")
  const [source, setSource] = useState(rule?.source ?? default_source)
  const [invoice_pattern, setInvoicePattern] = useState<Pattern>(() => (rule?.invoice_regex ? regex_to_pattern(rule.invoice_regex) : { kind: "exact", segments: [] }))
  const [remark_pattern, setRemarkPattern] = useState<Pattern>(() => (rule?.remark_regex ? regex_to_pattern(rule.remark_regex) : { kind: "contains", segments: [] }))
  const [error, setError] = useState<string | null>(null)

  const editing = rule !== null

  function submit(e: Event) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError("Category name is required.")
      return
    }
    const invoice_regex = pattern_is_empty(invoice_pattern) ? "" : pattern_to_regex(invoice_pattern)
    const remark_regex = pattern_is_empty(remark_pattern) ? "" : pattern_to_regex(remark_pattern)
    if (!editing && !invoice_regex && !remark_regex) {
      setError("Add an invoice number or description pattern so the rule can match deductions (or both).")
      return
    }
    const change: ProposedChange = editing
      ? {
          kind: "edit_rule",
          id: rule.id,
          name: name.trim(),
          description: description.trim(),
          invoice_regex,
          remark_regex,
        }
      : {
          kind: "create_rule",
          name: name.trim(),
          description: description.trim(),
          source,
          invoice_regex,
          remark_regex,
        }
    onPreview(change, editing ? `Edit rule: ${rule.name} — ${rule.description}` : `Add rule: ${name.trim()}`)
  }

  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit rule" : "Add category rule"}</DialogTitle>
          <DialogDescription>
            {editing && rule.is_global
              ? "This is a standard rule shared by all brands. Your changes create a customized copy that applies only to your organization; the standard rule stays available to others."
              : "Deductions matching the pattern are automatically assigned to this category on import."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit as any} class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Category name</Label>
              <Input list="category-name-options" value={name} onInput={(e: any) => setName(e.currentTarget.value)} placeholder="e.g. Spoilage" />
              <datalist id="category-name-options">
                {category_names.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={description} onInput={(e: any) => setDescription(e.currentTarget.value)} placeholder="e.g. Reclamation" />
            </div>
          </div>

          {!editing && (
            <div>
              <Label>Distributor / retailer</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger class="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((s) => (
                    <SelectItem key={s} value={s}>
                      {source_label(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <PatternBuilder
            label="Invoice number pattern"
            hint="Matched against the deduction's invoice number (case-sensitive)."
            pattern={invoice_pattern}
            onChange={setInvoicePattern}
            examples={example_invoice_numbers}
          />
          <PatternBuilder
            label="Description pattern"
            hint="Matched against the deduction's description (not case-sensitive)."
            pattern={remark_pattern}
            onChange={setRemarkPattern}
            examples={[]}
          />

          {error && <p class="text-sm text-red-600">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Preview changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function source_label(source: string): string {
  const special: Record<string, string> = { unfi: "UNFI", kehe: "KeHE", cands: "C&S", hyvee: "Hy-Vee", heb: "H-E-B", cvs: "CVS", ab: "AB", svharbor: "SV Harbor" }
  return special[source] ?? source.charAt(0).toUpperCase() + source.slice(1)
}

function pattern_is_empty(p: Pattern): boolean {
  if (p.kind === "advanced") return p.regex.trim() === ""
  return p.segments.length === 0 || p.segments.every((s) => s.kind === "literal" && s.text === "")
}

const KIND_LABELS: Record<string, string> = {
  exact: "Is exactly",
  starts_with: "Starts with",
  contains: "Contains",
  advanced: "Advanced (regex)",
}

interface PatternBuilderProps {
  label: string
  hint: string
  pattern: Pattern
  onChange: (p: Pattern) => void
  examples: string[]
}

/**
 * Plain-English pattern editor: a match kind plus a sequence of text / number chips.
 * Patterns too complex for the builder are edited as a raw regex under "Advanced".
 */
function PatternBuilder({ label, hint, pattern, onChange, examples }: PatternBuilderProps) {
  const matches = useMemo(
    () => (pattern_is_empty(pattern) ? [] : examples.filter((e) => pattern_matches(pattern, e)).slice(0, 3)),
    [pattern, examples],
  )

  function set_kind(kind: string) {
    if (kind === "advanced") {
      onChange({ kind: "advanced", regex: pattern_is_empty(pattern) ? "" : pattern_to_regex(pattern) })
    } else if (pattern.kind === "advanced") {
      const parsed = regex_to_pattern(pattern.regex)
      onChange(parsed.kind === "advanced" ? { kind: kind as any, segments: [] } : { ...parsed, kind: kind as any })
    } else {
      onChange({ ...pattern, kind: kind as any })
    }
  }

  function set_segment(i: number, seg: PatternSegment) {
    if (pattern.kind === "advanced") return
    const segments = pattern.segments.slice()
    segments[i] = seg
    onChange({ ...pattern, segments })
  }

  function remove_segment(i: number) {
    if (pattern.kind === "advanced") return
    onChange({ ...pattern, segments: pattern.segments.filter((_, j) => j !== i) })
  }

  function add_segment(seg: PatternSegment) {
    if (pattern.kind === "advanced") return
    onChange({ ...pattern, segments: [...pattern.segments, seg] })
  }

  return (
    <div class="border rounded-md p-3 space-y-2">
      <div class="flex items-baseline justify-between">
        <Label>{label}</Label>
        <span class="text-xs text-gray-400">{hint}</span>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <Select value={pattern.kind} onValueChange={set_kind}>
          <SelectTrigger class="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(KIND_LABELS).map(([value, text]) => (
              <SelectItem key={value} value={value}>
                {text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {pattern.kind === "advanced" ? (
          <Input
            class="font-mono flex-1 min-w-48"
            value={pattern.regex}
            onInput={(e: any) => onChange({ kind: "advanced", regex: e.currentTarget.value })}
            placeholder="regular expression"
          />
        ) : (
          <>
            {pattern.segments.map((seg, i) =>
              seg.kind === "numbers" ? (
                <span key={i} class="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-700">
                  numbers
                  <button type="button" class="text-gray-400 hover:text-gray-600" aria-label="Remove segment" onClick={() => remove_segment(i)}>
                    ×
                  </button>
                </span>
              ) : (
                <span key={i} class="inline-flex items-center gap-1">
                  <Input
                    class={`w-32 font-mono ${is_valid_literal(seg.text) ? "" : "border-red-400"}`}
                    value={seg.text}
                    onInput={(e: any) => set_segment(i, { kind: "literal", text: e.currentTarget.value })}
                    placeholder="text"
                  />
                  <button type="button" class="text-gray-400 hover:text-gray-600" aria-label="Remove segment" onClick={() => remove_segment(i)}>
                    ×
                  </button>
                </span>
              ),
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => add_segment({ kind: "literal", text: "" })}>
              + text
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => add_segment({ kind: "numbers" })}>
              + numbers
            </Button>
          </>
        )}
      </div>
      {!pattern_is_empty(pattern) && (
        <p class="text-xs text-gray-500">
          Invoice number {pattern_to_english(pattern)}
          {matches.length > 0 && (
            <>
              {" · matches e.g. "}
              {matches.map((m, i) => (
                <span key={m} class="font-mono">
                  {i > 0 ? ", " : ""}
                  {m}
                </span>
              ))}
            </>
          )}
        </p>
      )}
    </div>
  )
}
